/**
 * Header.tsx
 *
 * File-level: Main site header component shown on every page.
 * Provides the logo, primary navigation and mobile toggle.
 * The header keeps the visual frame 10% shorter than the original 120px height (108px).
 */

import React, { useRef, useState } from 'react'
import { Link } from 'react-router'
import { Menu } from 'lucide-react'
import MobileMenu from './HeaderMobileMenu'

/**
 * Header component
 *
 * Component-level: Renders the top navigation bar with brand logo and links.
 * Contains a responsive mobile toggle that opens a full-screen menu panel.
 */
export default function Header(): JSX.Element {
  const containerHeightClass = 'h-[108px]' // 120px reduced by 10% => 108px
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  /**
   * Reference to the mobile toggle button so focus can be restored after closing the menu.
   */
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null)

  /**
   * Toggle the mobile menu open state.
   *
   * @returns {void}
   */
  const toggleMobileMenu = () => {
    setIsMobileOpen((v) => !v)
  }

  /**
   * Close mobile menu handler.
   *
   * @returns {void}
   */
  const closeMobileMenu = () => {
    setIsMobileOpen(false)
  }

  /**
   * Renders the navigation links used in the header.
   *
   * @returns {JSX.Element} list of nav links
   */
  const NavLinks = () => (
    <nav className="hidden md:flex items-center gap-8">
      <Link to="/" className="text-lg font-semibold text-gray-700 hover:text-gray-900 transition-colors">
        Home
      </Link>
      <Link to="/about" className="text-lg font-semibold text-gray-700 hover:text-gray-900 transition-colors">
        About Us
      </Link>
      <Link to="/services" className="text-lg font-semibold text-gray-700 hover:text-gray-900 transition-colors">
        Services
      </Link>
      <Link to="/portfolio" className="text-lg font-semibold text-gray-700 hover:text-gray-900 transition-colors">
        Portfolio
      </Link>
      <Link to="/team" className="text-lg font-semibold text-gray-700 hover:text-gray-900 transition-colors">
        Team
      </Link>
      <Link
        to="/contact"
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 shadow py-2 h-11 px-6 text-base bg-transparent text-gray-700 hover:bg-gray-100"
      >
        Contact
      </Link>
    </nav>
  )

  return (
    <header className="w-full bg-transparent border-b border-transparent">
      <div className={`mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8`}>
        {/* Main row: height reduced by 10% compared to the original 120px */}
        <div className={`flex items-center justify-between ${containerHeightClass}`}>
          {/* Logo (keeps its aspect ratio and fits inside the shorter frame) */}
          <Link aria-label="Go to home" to="/" className="inline-flex items-center shrink-0">
            <img
              src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/f2dcbeba-4716-4434-8b8b-01acce34f085.png"
              alt="Edicoo logo"
              className="block object-contain"
              style={{ height: 108, width: 'auto' }}
              onError={(e) => {
                // Fallback small logo if original fails to load
                ;(e.currentTarget as HTMLImageElement).src =
                  'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/266987bf-c3f8-4c4b-98fa-02f33d6e57a7.png'
              }}
            />
          </Link>

          {/* Desktop Nav */}
          <NavLinks />

          {/* Mobile menu toggle */}
          <button
            ref={toggleButtonRef}
            type="button"
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation"
            aria-expanded={isMobileOpen}
            className="inline-flex items-center justify-center md:hidden rounded-md p-2 text-gray-700 hover:bg-gray-100"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <MobileMenu isOpen={isMobileOpen} onClose={closeMobileMenu} returnFocusRef={toggleButtonRef} />
    </header>
  )
}